import { Input, Panel } from '@lib/components'
import type { ID } from '@octopus-do/octopus-project-core'
import { ThemeColors } from '@octopus-do/octopus-storybook'
import { artBoardActions } from '@store/reducers/artboard'
import type { AppState } from '@store/types'
import type React from 'react'
import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { clsJoin } from '@/utils'
import { ContentResults, SectionGroup, TreeRow } from './components'
import {
  useFilteredStructure,
  useJumpToPage,
  useStructureSearch,
  useStructureTree,
} from './hooks'
import css from './styles.module.css'

export const StructureSidebar: React.FC = () => {
  const dispatch = useDispatch()

  const [query, setQuery] = useState('')

  const sections = useStructureTree()
  const { pageIds, result, hasContentMatches } = useStructureSearch(query)
  const visibleSections = useFilteredStructure(sections, query, pageIds)

  const jumpToPage = useJumpToPage()

  const selectedId = useSelector<AppState, ID | null>(
    ({ canvas }) => canvas.node.selectedNodes[0] ?? null
  )

  const handleClose = () =>
    dispatch(artBoardActions.showStructure({ showStructure: false }))

  const [firstSection, ...restSections] = visibleSections

  // While filtering, a section that lost every page is dropped from the render
  // (it keeps its slot in visibleSections, so the heading-less first section
  // stays the first section).
  const visibleGroups = query
    ? restSections.filter((section) => section.roots.length)
    : restSections

  const noPages = visibleSections.every((section) => !section.roots.length)

  return (
    <Panel
      title="Structure"
      containerClassName={clsJoin(css.structure_sidebar, ThemeColors.light)}
      onClose={handleClose}
    >
      <div className={css.search}>
        <Input
          size="medium"
          type="normal"
          can="clear"
          placeholder="Search"
          focusOnRender
          onChange={setQuery}
        />
      </div>

      <div className={css.tree}>
        {noPages && !hasContentMatches && (
          <div className={css.empty}>{query ? 'No matches' : 'No pages'}</div>
        )}

        {/* The first section carries the sitemap's own root pages, so it is
            shown without a heading — a lone "Main" label above "Home" is noise
            in the common single-section project. */}
        {firstSection?.roots.map((node) => (
          <TreeRow
            key={node.id}
            node={node}
            selectedId={selectedId}
            onSelect={jumpToPage}
          />
        ))}

        {visibleGroups.map((section, index) => (
          <SectionGroup
            key={section.id}
            section={section}
            selectedId={selectedId}
            showDivider={index === 0 && Boolean(firstSection?.roots.length)}
            onSelect={jumpToPage}
          />
        ))}

        {hasContentMatches && <ContentResults result={result} />}
      </div>
    </Panel>
  )
}
